import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LazyRichTextEditor } from '../base/form/FormRichTextEditor';
import { useGuideBuilderGuide } from '../../contexts/GuideBuilderContext';
import { useUpdateStep } from '../../hooks/useGuides';
import { useLanguage } from '@/contexts/LanguageContext';
import { GuideStepType } from '@/services/apis/guides';
import StepEditorWrapper from './StepEditorWrapper';

interface RichTextStepEditorFormData {
  content: string;
}

const RichTextStepEditorForm: React.FC = () => {
  const {
    guide: { id: guideId },
    state: { selectedStep },
  } = useGuideBuilderGuide();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation('admin');
  const updateStep = useUpdateStep(guideId || '');
  const [shouldAutoSave, setShouldAutoSave] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isValid },
  } = useForm<RichTextStepEditorFormData>({
    defaultValues: {
      content: selectedStep?.content?.text || '',
    },
  });

  // Reset form when selectedStep or language changes
  useEffect(() => {
    if (selectedStep) {
      reset({
        content: selectedStep.content?.text || '',
      });
      setShouldAutoSave(false);
    }
  }, [selectedStep, currentLanguage, reset]);

  // Watch form values and trigger auto-save when dirty and valid
  const formValues = watch();
  useEffect(() => {
    if (isDirty && isValid) {
      setShouldAutoSave(true);
    }
  }, [formValues, isDirty, isValid]);

  const handleSave = handleSubmit(async (data) => {
    if (!selectedStep?.id) return;

    try {
      await updateStep.mutateAsync({
        stepId: selectedStep.id,
        data: {
          content: {
            text: data.content,
          },
        },
      });
      // Reset form after successful save to mark as not dirty
      reset(data, { keepValues: true });
    } catch (error) {
      console.error('Error saving step content:', error);
    }
  });

  const handleSavePress = () => {
    handleSave();
  };

  const handleAutoSaveComplete = useCallback(() => {
    setShouldAutoSave(false);
    // Reset form to mark as not dirty after auto-save
    const currentValues = watch();
    reset(currentValues, { keepValues: true });
  }, [watch, reset]);

  if (!selectedStep) {
    return null;
  }

  return (
    <StepEditorWrapper
      title={t('guides.builder.stepEditor.richTextStep') || 'Rich Text Step'}
      stepType={GuideStepType.RICH_TEXT}
      onSave={handleSavePress}
      isSaveDisabled={!isDirty || updateStep.isPending}
      isPending={updateStep.isPending}
      shouldAutoSave={shouldAutoSave}
      onAutoSaveComplete={handleAutoSaveComplete}
    >
      <LazyRichTextEditor control={control} name="content" />
    </StepEditorWrapper>
  );
};

export default RichTextStepEditorForm;
